/**
 * Datas para a tela.
 *
 * A data do evento vem do servidor como texto livre — o protocolo não promete
 * formato —, então nada aqui pode assumir ISO. Quando não dá para interpretar,
 * o texto cru é mostrado como veio: melhor o operador ler "31/12 virada" do que
 * ver um "Data inválida" que não ajuda ninguém na portaria.
 *
 * Hora sempre em 24 h e dia em DD/MM, montados aqui e não pelo locale do
 * aparelho: é o formato que a portaria lê nos dois países, e um aparelho
 * configurado em inglês não pode virar "9:00 PM" no meio da fila.
 */

import {txt} from './idioma';

const DOIS = (n: number) => String(n).padStart(2, '0');

/**
 * Interpreta o que o servidor mandou. Aceita ISO, `YYYY-MM-DD HH:MM:SS` (o
 * formato do TMobi) e `DD/MM/YYYY HH:MM`.
 */
export function lerData(bruta?: string): Date | null {
  if (!bruta) {
    return null;
  }
  const texto = bruta.trim();

  // Com fuso explícito — o `Z` do `toISOString()`, que é como a validação é
  // gravada — quem converte para o relógio da portaria é o próprio `Date`. Ler
  // os dígitos como se fossem locais erraria a hora do fechamento em três
  // horas e transformaria "agora" em "há 180 min".
  if (/([Zz]|[+-]\d{2}:?\d{2})$/.test(texto)) {
    const comFuso = new Date(texto);
    if (!Number.isNaN(comFuso.getTime())) {
      return comFuso;
    }
  }

  const brasileira = texto.match(
    /^(\d{2})\/(\d{2})\/(\d{4})(?:[ T](\d{2}):(\d{2}))?/,
  );
  if (brasileira) {
    const [, d, m, a, hh, mm] = brasileira;
    return new Date(Number(a), Number(m) - 1, Number(d), Number(hh ?? 0), Number(mm ?? 0));
  }

  const isoLike = texto.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/);
  if (isoLike) {
    const [, a, m, d, hh, mm] = isoLike;
    return new Date(Number(a), Number(m) - 1, Number(d), Number(hh ?? 0), Number(mm ?? 0));
  }

  const solta = new Date(texto);
  return Number.isNaN(solta.getTime()) ? null : solta;
}

export function ehHoje(data: Date): boolean {
  const agora = new Date();
  return (
    data.getFullYear() === agora.getFullYear() &&
    data.getMonth() === agora.getMonth() &&
    data.getDate() === agora.getDate()
  );
}

/** Rótulo do cartão de evento: "Hoje · 21:00" ou "29/08 · 22:00". */
export function quandoAcontece(bruta?: string): {hoje: boolean; dia: string; hora: string} {
  const data = lerData(bruta);
  if (!data) {
    return {hoje: false, dia: (bruta ?? '').trim(), hora: ''};
  }

  const hora = `${DOIS(data.getHours())}:${DOIS(data.getMinutes())}`;
  if (ehHoje(data)) {
    return {hoje: true, dia: txt('Hoje'), hora};
  }
  return {hoje: false, dia: `${DOIS(data.getDate())}/${DOIS(data.getMonth() + 1)}`, hora};
}

/** Só a hora de um instante gravado — usado no relatório de fechamento. */
export function horaDe(iso?: string): string {
  const data = lerData(iso);
  return data ? `${DOIS(data.getHours())}:${DOIS(data.getMinutes())}` : '—';
}

/** "há 2 min" para a última carga do evento. */
export function desdeQuando(iso?: string): string {
  const data = lerData(iso);
  if (!data) {
    return '';
  }
  const minutos = Math.floor((Date.now() - data.getTime()) / 60000);
  if (minutos < 1) {
    return txt('agora');
  }
  if (minutos < 60) {
    return txt('há {quantos} min', {quantos: minutos});
  }
  const horas = Math.floor(minutos / 60);
  return horas < 24
    ? txt('há {quantos} h', {quantos: horas})
    : txt('há {quantos} d', {quantos: Math.floor(horas / 24)});
}

/**
 * Milhar com ponto, como a portaria lê: 12.480.
 *
 * Na mão em vez de `toLocaleString('pt-BR')` porque a formatação depende dos
 * dados de locale do motor JS, e um contador que aparece como "12480" num
 * aparelho e "12.480" no outro confunde a conferência do fechamento.
 */
export function comMilhar(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/**
 * Número com o substantivo concordando: "1 ingresso", "18 ingressos".
 *
 * Vale o cuidado porque esses contadores viram frase na tela e o operador lê em
 * voz alta para o supervisor — "um ingressos" denuncia software mal-acabado
 * justamente na tela em que ele precisa confiar na conta.
 *
 * As duas formas chegam prontas de quem chama (`txt('ingresso')`), para o
 * conferidor de idioma continuar enxergando o texto onde ele é escrito.
 */
export function contagem(n: number, singular: string, plural: string): string {
  return `${comMilhar(n)} ${n === 1 ? singular : plural}`;
}
